import { colors, s16, s4 } from '@ardoq/design-tokens';
import { Checkbox } from '@ardoq/forms';
import { Icon, IconName } from '@ardoq/icons';
import { WithPopover } from '@ardoq/popovers';
import styled from 'styled-components';
import { FlexBox } from '@ardoq/layout';
import { t, type I18nLocale } from '@ardoq/i18n';
import { ComponentSelectionMode } from './types';

type SearchResultCountAndSelectAllProps = {
  totalCount: number;
  selectedComponentsCount: number;
  allComponentsSelected: boolean;
  selectionClearedOnSelectAllCount: number | null;
  isAdvancedSearch: boolean;
  isSelectAllDisabled: boolean;
  onToggleAllComponents: () => void;
  selectionMode?: ComponentSelectionMode;
  i18nLocale: I18nLocale;
};

const getSelectedText = ({
  selectedComponentsCount,
  selectionMode,
  i18nLocale,
}: {
  selectedComponentsCount: number;
  selectionMode: ComponentSelectionMode;
  i18nLocale: I18nLocale;
}) => {
  if (selectedComponentsCount > 0) {
    return t(
      i18nLocale,
      'viewpointBuilder:selectContext.results.selectedCount',
      { count: selectedComponentsCount },
      '{{count}} selected'
    );
  }

  if (selectionMode === 'single') {
    return t(
      i18nLocale,
      'viewpointBuilder:selectContext.results.selectOne',
      'Select a component to continue'
    );
  }

  return t(
    i18nLocale,
    'viewpointBuilder:selectContext.results.selectAtLeastOne',
    'Select at least one component to continue'
  );
};

const SearchResultCountAndSelectAll = ({
  totalCount,
  allComponentsSelected,
  selectionClearedOnSelectAllCount,
  isAdvancedSearch,
  onToggleAllComponents,
  selectedComponentsCount,
  isSelectAllDisabled,
  selectionMode = 'multiple',
  i18nLocale,
}: SearchResultCountAndSelectAllProps) => {
  const selectedText = getSelectedText({
    selectedComponentsCount,
    selectionMode,
    i18nLocale,
  });
  return (
    <Wrapper>
      {selectionMode === 'multiple' ? (
        <Checkbox
          aria-labelledby="select-all-results"
          isDisabled={isSelectAllDisabled}
          isChecked={allComponentsSelected}
          onChange={onToggleAllComponents}
        >
          <span id="select-all-results">
            {t(
              i18nLocale,
              'viewpointBuilder:selectContext.results.selectAll',
              'Select all'
            )}
          </span>
        </Checkbox>
      ) : (
        <span />
      )}
      <FlexBox align="center">
        {t(
          i18nLocale,
          'viewpointBuilder:selectContext.results.resultsCount',
          { count: totalCount },
          '{{count}} results'
        )}{' '}
        &bull; {selectedText}
        {selectionClearedOnSelectAllCount !== null &&
          selectionClearedOnSelectAllCount > 0 && (
            <SelectionClearedInfo
              isAdvancedSearch={isAdvancedSearch}
              selectionClearedOnSelectAllCount={
                selectionClearedOnSelectAllCount
              }
              i18nLocale={i18nLocale}
            />
          )}
      </FlexBox>
    </Wrapper>
  );
};

const SelectionClearedInfo = ({
  isAdvancedSearch,
  selectionClearedOnSelectAllCount,
  i18nLocale,
}: {
  isAdvancedSearch: boolean;
  selectionClearedOnSelectAllCount: number;
  i18nLocale: I18nLocale;
}) => (
  <FlexBox align="center" marginLeft="medium" gap="xsmall">
    <WithPopover
      content={() => (
        <PopoverContent>
          {t(
            i18nLocale,
            'viewpointBuilder:selectContext.results.clearedPopover.intro',
            'Clicking select all automatically clears any components you have selected before.'
          )}{' '}
          {isAdvancedSearch
            ? t(
                i18nLocale,
                'viewpointBuilder:selectContext.results.clearedPopover.refineQuery',
                'Try refining your query for more control over selections.'
              )
            : t(
                i18nLocale,
                'viewpointBuilder:selectContext.results.clearedPopover.useAdvanced',
                'Try using Advanced search for more control over selections.'
              )}
        </PopoverContent>
      )}
    >
      <Icon iconName={IconName.INFO} color={colors.blue600}></Icon>
    </WithPopover>
    <span>
      {t(
        i18nLocale,
        'viewpointBuilder:selectContext.results.previousSelectionsCleared',
        { count: selectionClearedOnSelectAllCount },
        '{{count}} previous selections cleared'
      )}
    </span>
  </FlexBox>
);

const PopoverContent = styled.div`
  width: 272px;
`;

const Wrapper = styled.div`
  color: ${colors.grey500};
  font-size: ${s16};
  display: flex;
  justify-content: space-between;
  padding: ${s16};
  border-bottom: 1px solid ${colors.grey300};
  margin-bottom: ${s4};
`;

export { SearchResultCountAndSelectAll };
